import React, { useState, useMemo, useCallback } from 'react';
import { Code, Eye, Copy, Check, Download, FileText, Bold, Italic, Heading1, Heading2, Link, List, Terminal } from 'lucide-react';

const SAMPLE_MARKDOWN = `# Welcome to Markdown

This is a **bold** and *italic* text demo with ~~strikethrough~~.

## Features

- Headings (h1-h3)
- **Bold** and *italic* text
- ~~Strikethrough~~
- [Links](https://example.com)
- Images, code blocks, and more

### Code Example

Inline \`code\` looks like this.

\`\`\`javascript
function greet(name) {
  return "Hello, " + name;
}
\`\`\`

> This is a blockquote.
> It can span multiple lines.

### Ordered List

1. First item
2. Second item
3. Third item

---

### Table

| Feature | Status |
|---------|--------|
| Headings | Done |
| Bold/Italic | Done |
| Tables | Done |

That's all for now!`;

function markdownToHtml(md: string): string {
  let html = md;

  // Code blocks (``` ... ```) — must be processed before inline rules
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_match, _lang, code) => {
    const escaped = code.replace(/</g, '&lt;').replace(/>/g, '&gt;').trimEnd();
    return `<pre><code>${escaped}</code></pre>`;
  });

  // Inline code (preserve as-is, must be before other inline rules)
  const inlineCodePlaceholders: string[] = [];
  html = html.replace(/`([^`]+)`/g, (_match, code) => {
    const idx = inlineCodePlaceholders.length;
    inlineCodePlaceholders.push(`<code>${code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code>`);
    return `%%ICODE_${idx}%%`;
  });

  // Tables
  html = html.replace(/^(\|.+\|)\n(\|[\s:|-]+\|)\n((?:\|.+\|\n?)*)/gm, (match) => {
    const rows = match.trim().split('\n');
    if (rows.length < 2) return match;
    const headerCells = rows[0].split('|').filter(c => c.trim());
    const bodyRows = rows.slice(2);

    let table = '<table><thead><tr>';
    for (const cell of headerCells) {
      table += `<th>${cell.trim()}</th>`;
    }
    table += '</tr></thead><tbody>';
    for (const row of bodyRows) {
      if (!row.trim()) continue;
      const cells = row.split('|').filter(c => c.trim());
      table += '<tr>';
      for (const cell of cells) {
        table += `<td>${cell.trim()}</td>`;
      }
      table += '</tr>';
    }
    table += '</tbody></table>';
    return table;
  });

  // Headings
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Horizontal rules
  html = html.replace(/^---+$/gm, '<hr>');

  // Blockquotes
  html = html.replace(/^(?:>\s?(.*)(?:\n|$))+/gm, (match) => {
    const content = match.replace(/^>\s?/gm, '').trim();
    return `<blockquote>${content}</blockquote>`;
  });

  // Bold & italic combined
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Strikethrough
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1">');
  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Unordered lists
  html = html.replace(/^(?:[-*]\s+.+(?:\n|$))+/gm, (match) => {
    const items = match.trim().split('\n').map(line =>
      `<li>${line.replace(/^[-*]\s+/, '')}</li>`
    ).join('');
    return `<ul>${items}</ul>`;
  });

  // Ordered lists
  html = html.replace(/^(?:\d+\.\s+.+(?:\n|$))+/gm, (match) => {
    const items = match.trim().split('\n').map(line =>
      `<li>${line.replace(/^\d+\.\s+/, '')}</li>`
    ).join('');
    return `<ol>${items}</ol>`;
  });

  // Paragraphs: wrap remaining loose text blocks
  html = html.replace(/^(?!<[a-z]|%%ICODE)(.+)$/gm, (match, content) => {
    const trimmed = content.trim();
    if (!trimmed) return '';
    return `<p>${trimmed}</p>`;
  });

  // Restore inline code placeholders
  for (let i = 0; i < inlineCodePlaceholders.length; i++) {
    html = html.replace(`%%ICODE_${i}%%`, inlineCodePlaceholders[i]);
  }

  // Clean up extra blank lines
  html = html.replace(/\n{3,}/g, '\n\n').trim();

  return html;
}

const MarkdownToHtml: React.FC = () => {
  const [markdown, setMarkdown] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [copied, setCopied] = useState(false);

  const htmlOutput = useMemo(() => markdownToHtml(markdown), [markdown]);

  const insertAtCursor = useCallback((before: string, after: string, placeholder: string) => {
    const textarea = document.getElementById('md-input') as HTMLTextAreaElement | null;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = markdown.substring(start, end) || placeholder;
    const newText = markdown.substring(0, start) + before + selected + after + markdown.substring(end);
    setMarkdown(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  }, [markdown]);

  const copyHtml = () => {
    navigator.clipboard.writeText(htmlOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadHtml = () => {
    const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Converted Markdown</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; max-width: 800px; margin: 2rem auto; padding: 0 1rem; line-height: 1.6; color: #333; }
  pre { background: #f5f5f5; padding: 1rem; border-radius: 4px; overflow-x: auto; }
  code { background: #f0f0f0; padding: 0.2em 0.4em; border-radius: 3px; font-size: 0.9em; }
  pre code { background: none; padding: 0; }
  blockquote { border-left: 4px solid #ddd; margin: 1rem 0; padding: 0.5rem 1rem; color: #666; }
  table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
  th, td { border: 1px solid #ddd; padding: 0.5rem; text-align: left; }
  th { background: #f5f5f5; }
  hr { border: none; border-top: 1px solid #ddd; margin: 2rem 0; }
  img { max-width: 100%; }
</style>
</head>
<body>
${htmlOutput}
</body>
</html>`;
    const blob = new Blob([fullHtml], { type: 'text/html' });
    const link = document.createElement('a');
    link.download = 'converted.html';
    link.href = URL.createObjectURL(blob);
    link.click();
  };

  const toolbarButtons = [
    { icon: Bold, label: 'Bold', action: () => insertAtCursor('**', '**', 'bold text') },
    { icon: Italic, label: 'Italic', action: () => insertAtCursor('*', '*', 'italic text') },
    { icon: Heading1, label: 'H1', action: () => insertAtCursor('# ', '', 'Heading 1') },
    { icon: Heading2, label: 'H2', action: () => insertAtCursor('## ', '', 'Heading 2') },
    { icon: Link, label: 'Link', action: () => insertAtCursor('[', '](https://)', 'link text') },
    { icon: List, label: 'List', action: () => insertAtCursor('- ', '', 'List item') },
    { icon: Terminal, label: 'Code', action: () => insertAtCursor('`', '`', 'code') },
  ];

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-violet-100 rounded-lg">
          <Code className="w-6 h-6 text-violet-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Markdown to HTML</h1>
          <p className="text-gray-500 text-sm">Convert Markdown to HTML with live preview</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 mb-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
        {toolbarButtons.map(({ icon: Icon, label, action }) => (
          <button
            key={label}
            onClick={action}
            title={label}
            className="p-2 text-gray-600 hover:text-violet-600 hover:bg-violet-50 rounded-md transition-colors"
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={() => setMarkdown(SAMPLE_MARKDOWN)}
          className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-md flex items-center gap-1"
        >
          <FileText className="w-3 h-3" /> Sample
        </button>
        <button
          onClick={() => setShowPreview(!showPreview)}
          className={`px-3 py-1.5 text-xs rounded-md flex items-center gap-1 border ${
            showPreview ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200'
          }`}
        >
          <Eye className="w-3 h-3" /> Preview
        </button>
      </div>

      {/* Editor panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Markdown input */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Markdown</label>
          <textarea
            id="md-input"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            placeholder="Type your Markdown here..."
            className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-y font-mono text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-gray-800"
          />
        </div>

        {/* Output */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">
            {showPreview ? 'Preview' : 'HTML Output'}
          </label>
          {showPreview ? (
            <div
              className="w-full h-96 p-4 border border-gray-300 rounded-lg overflow-y-auto bg-white prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: htmlOutput }}
              style={{
                // Basic inline styles for rendered HTML elements
              }}
            />
          ) : (
            <textarea
              readOnly
              value={htmlOutput}
              className="w-full h-96 p-4 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm text-gray-800 resize-y"
            />
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={copyHtml}
          disabled={!htmlOutput}
          className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy HTML'}
        </button>
        <button
          onClick={downloadHtml}
          disabled={!htmlOutput}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm flex items-center gap-2"
        >
          <Download className="w-4 h-4" /> Download .html
        </button>
        <button
          onClick={() => setMarkdown('')}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
        >
          Clear
        </button>
      </div>

      {/* Supported syntax reference */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Supported Syntax</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-600">
          <span># Headings (h1-h3)</span>
          <span>**Bold** *Italic*</span>
          <span>~~Strikethrough~~</span>
          <span>[Links](url)</span>
          <span>![Images](url)</span>
          <span>- Unordered lists</span>
          <span>1. Ordered lists</span>
          <span>&gt; Blockquotes</span>
          <span>``` Code blocks</span>
          <span>`Inline code`</span>
          <span>--- Horizontal rules</span>
          <span>| Tables |</span>
        </div>
      </div>
    </div>
  );
};

export default MarkdownToHtml;
